import { Line } from './types/line';

export const enum TransportMode {
  BUS = "BUS",
  TRAM = "TRAM",
  RAIL = "RAIL",
  SUBWAY = "SUBWAY",
  FERRY = "FERRY",
  CABLECAR = "CABLECAR",
  GONDOLA = "GONDOLA",
  FUNICULAR = "FUNICULAR"
}

export interface DeparturesOptions {
  date?: string,
  time?: string,
  limit?: number,
  nbpasttimes?: number,
  pastsince?: number,
  simulateOccupancy?: boolean,
}

export interface Destination {
  mnemoService: string;
  serviceId: string;
  firstTime: TimeData;
  times: TimeData[];
  line: Line;
  serviceDisruption?: boolean;
  globalDisruption?: boolean;
  busStopDisruption?: boolean;
  direction?: string;
  dirlib?: string;
  fromlib?: string;
}

export function mergeTimes(timesA: TimeData[], timesB: TimeData[]): TimeData[] {
  const atimes: string[] = timesA.map(t => t.hourDisplayByStateCode());
  for (let t of timesB) {
    if (atimes.indexOf(t.hourDisplayByStateCode()) < 0) {
      timesA.push(t);
    }
  }

  return timesA.sort((a, b) => {
    let aord = a.stringOrder();
    let bord = b.stringOrder();
    if (aord == bord) return 0;
    return aord < bord ? -1 : 1;
  });
}

export function simulateOccupancy(times: TimeData[]) {
  return times.map(t => {
    // Simulate case of no occupancy data available
    let occupancyDataAvailable = Math.floor(Math.random() * 2);
    if (occupancyDataAvailable == 0) {
      return t;
    }
    // Generate fake occupancy data
    let rawOccupancy = Math.floor(Math.random() * 101);

    if (rawOccupancy == 0) {
      t.occupancy = '0';
    } else if (rawOccupancy > 0 && rawOccupancy < 20) {
      t.occupancy = '0-20';
    } else if (rawOccupancy >= 20 && rawOccupancy < 70) {
      t.occupancy = '20-70';
    } else if (rawOccupancy >= 70 && rawOccupancy < 90) {
      t.occupancy = '70-90';
    } else if (rawOccupancy >= 90 && rawOccupancy < 100) {
      t.occupancy = '90-100';
    }  else if (rawOccupancy == 100) {
      t.occupancy = '100';
    }
    return t;
  });
}

export class TimeData {
  isLive: boolean;
  isCanceled: boolean;
  equipment?: string;
  type?: string;
  reliability?: string;
  isDeparture?: boolean;
  isTerminus?: boolean;
  date?: Date;
  minutes?: number;
  time?: string;
  shape?: [];
  nameDest: string;
  tripHeadsign?: string;
  occupancy?: string;
  journeyId?: string;

  // Number max of minutes before schedule to consider the schedule to be "due"
  readonly dueMaxMinutes = 1;

  constructor(isLive: boolean, nameDest: string, isCanceled: boolean) {
    this.isLive = isLive;
    this.isCanceled = isCanceled;
    this.nameDest = nameDest;
  }
  setData(objData: any){
    this.reliability = objData.reliability;
    this.date = objData.date;
    this.minutes = objData.minutes;
    this.time = objData.time;
    this.shape = objData.shape;
    this.isTerminus = objData.isTerminus;
    this.isCanceled = objData.isCanceled;
    this.isDeparture = objData.isDeparture;
    this.occupancy = objData.occupancy;
    this.tripHeadsign = objData.tripHeadsign;
    this.occupancy = objData.occupancy;
    this.journeyId = objData.journeyId;
  }
  isRealTime() {
    return (
      this.reliability == "D" ||
      this.reliability == "R" ||
      (this.minutes && (this.reliability == "F" && (this.minutes < 0 || this.minutes > this.dueMaxMinutes)))
    );
  }
  isPastRealTime() {
    return this.minutes < 0;
  }
  isPerturbation() {
    return (
      this.reliability == "B" ||
      this.reliability == "I" ||
      this.reliability == "N"
    );
  }
  stringOrder() {
    let minutes = this.minutes;
    if (!minutes || minutes < this.dueMaxMinutes) {
      return "0";
    } else if (minutes < 60) {
      return minutes.toString();
    }

    let timeDate = this.date;
    if (!timeDate) {
      timeDate = new Date();
    }
    let strDate = timeDate.toISOString().split('T')[0];
    return "A" + strDate + this.time;
  }
  /**
   * Format schedule time
   * @param alwaysShowHours - true : always display raw time schedule from Orchestra. false : display "n min." when schedule is in under 60 minutes from now
   * @returns schedule time formatted
   */
  hourDisplayByStateCode(alwaysShowHours: boolean = false): string {
    let minutes = this.minutes;
    let time = this.time;

    // dans tous les cas les horaires annulés sont affichés en absolu
    if(this.isCanceled){
      return time || "";
    }

    switch (this.reliability) {
      case "R":
      case "D":
      case "F":
      case "H":
        if (!minutes || (0 <= minutes && minutes <= this.dueMaxMinutes)) {
          return "now";
        } else if (!alwaysShowHours && (0 < minutes && minutes < 60)) {
          return minutes + " min.";
        } else {
          return time || "";
        }
      case "B":
      case "I":
      case "N":
      case "V":
        if (!minutes || (0 <= minutes && minutes < 1)) {
          return time || "";
        } else if (!alwaysShowHours && (0 < minutes && minutes < 60)) {
          return minutes + " min.";
        } else {
          return time || "";
        }
      case "T":
        return time || "";
    }
    return time || "";
  }
}

export interface GtfsData {
  stops: { [key: string]: StopPoint };
  lines: { [key: string]: Line };
}

export class  LineInfos {
  dir0_label: string;
  dir1_label: string;

  constructor(dir0_label: string, dir1_label: string) {
    this.dir0_label = dir0_label;
    this.dir1_label = dir1_label;
  }
}

export interface ApiColors {
  name: string;
  bgcolor: string;
  color: string;
}

export interface StopsByLineDict {
  [key: string]: StopPoint[];
}

export interface StopsByLine {
  line: Line;
  stopsByDestinations: StopsByDestination[];
}
export interface StopsByDestination {
  destinationName: string;
  stopsRefs: string[];
}

export interface StopPoint {
  name?: string;
  x?: number;
  y?: number;
  ref?: string;
  city?: string;
  stopId?: string;
  lines?: string[];
  dests?: Dest[];

  isCommercial?: boolean;
  isCurrent?: boolean;
  spoints?: StopPoint[];
}

export interface Dest {
  id: string;
  name: string;
  service: string; // => Line.ref
}
export interface PoiType {
  id: number;
  name?: string;
  description?: string;
  icon?: string;
  iconUri?: string;
  marker?: string;
  checked?: boolean;
  class?: string;
  link?: string;
}
export interface Poi {
  ref: number;
  field_type_poi: number;
  name: string;
  description?: string;
  link?: string;
  x: number;
  y: number;
}
export interface CalendarPerimeter {
  start: string,
  end: string,
}
